<template>
  <b-sidebar
    :id="drawerId"
    v-model="localVisible"
    right
    shadow
    backdrop
    :width="width"
    no-header
    bg-variant="white"
    text-variant="dark"
    sidebar-class="custom-floating-drawer"
    @hidden="handleHidden"
  >
    <div class="d-flex flex-column h-100 p-[24px] topup-drawer-content">
      <div class="d-flex align-items-start justify-content-between mb-2">
        <div>
          <h3 class="mb-50 text-black font-weight-bolder" style="font-size: 20px;">
            Top Up Kredit Komed
          </h3>
          <p class="mb-0" style="font-size: 14px; line-height: 1.4;">
            Yuk, isi kredit Komed pakai Kompay—langsung siap buat generate video AI
          </p>
        </div>
        <b-button
          variant="flat-secondary"
          class="btn-icon close-btn flex-shrink-0"
          @click="closeDrawer"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#626262"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </b-button>
      </div>

      <div class="mb-2">
        <div class="balance-box">
          <span class="balance-label">Saldo Kompay</span>
          <span class="balance-amount">Rp. {{ formatCurrency(balance) }}</span>
        </div>
      </div>

      <b-form-group>
        <template #label>
          <span class="font-weight-semibold" style="color: #333333; font-size: 14px;">
            Nominal Top Up<span class="text-danger">*</span>
          </span>
        </template>
        <b-form-input
          id="nominal-topup-komed"
          :value="nominal"
          placeholder="0"
          :state="showNominalError ? false : null"
          class="custom-input-topup"
          @input="onNominalInput"
          @keypress="onlyNumber"
        />
        <b-form-invalid-feedback :state="showNominalError ? false : null">
          <template v-if="hasNominalInput && insufficientBalance()">
            Saldo Kompay-mu tidak mencukupi.
            <router-link
              to="/"
              class="text-danger"
              style="text-decoration: underline;"
            >
              Top up saldo Kompay sekarang!
            </router-link>
          </template>
          <template v-else>
            {{ nominalErrorMessage }}
          </template>
        </b-form-invalid-feedback>

        <div class="mt-[15px]">
          <small class="d-block text-[#0A0A0A] mb-[5px]" style="font-size: 13px;">Rp1 = 1 kredit</small>
          <small class="d-block text-[#0A0A0A]" style="font-size: 13px;">Contoh: Rp10.000 = 10.000 kredit</small>
        </div>
      </b-form-group>

      <b-row class="mb-2 mx-0">
        <b-col
          v-for="amount in quickTopupAmounts"
          :key="amount"
          cols="4"
          class="px-25"
        >
          <b-button
            variant="outline-secondary"
            class="w-100 preset-btn"
            @click="setTopupNominal(amount)"
          >
            {{ formatCurrency(amount) }}
          </b-button>
        </b-col>
      </b-row>

      <div class="mt-auto d-flex justify-content-between pt-1 border-top w-100 gap-2">
        <div style="flex: 1;" class="mr-50">
          <b-button
            variant="outline-secondary"
            class="w-100 btn-cancel"
            @click="closeDrawer"
          >
            Batal
          </b-button>
        </div>
        <div style="flex: 1;">
          <b-button
            variant="primary"
            class="w-100 btn-submit"
            :disabled="isSubmitDisabled"
            @click="handleSubmit"
          >
            <b-spinner
              v-if="submitLoading"
              small
              class="mr-50"
            />
            Top Up Sekarang
          </b-button>
        </div>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    drawerId: {
      type: String,
      default: 'drawer-topup-kredit-komed',
    },
    width: {
      type: String,
      default: '460px',
    },
    balance: {
      type: Number,
      default: 0,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
    quickTopupAmounts: {
      type: Array,
      default: () => [50000, 100000, 150000],
    },
    showCurrencyPrefix: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      localVisible: false,
      nominal: '',
      submitAttempted: false,
      minimumTopupAmount: 0,
      isNominalDirty: false,
    }
  },
  computed: {
    nominalNumber() {
      return Number((this.nominal || '').replace(/\D/g, '')) || 0
    },
    hasNominalInput() {
      return String(this.nominal || '').trim() !== ''
    },
    showNominalError() {
      if (!this.hasNominalInput) {
        return this.submitAttempted || this.isNominalDirty
      }

      return this.isNominalBelowMinimum() || this.insufficientBalance()
    },
    nominalErrorMessage() {
      if (!this.hasNominalInput) {
        return 'Masukkan nominal top up'
      }

      if (this.insufficientBalance()) {
        return 'Saldo Kompay-mu tidak mencukupi. Top up saldo Kompay sekarang!'
      }

      return `Nominal top up minimal Rp ${this.formatCurrency(this.minimumTopupAmount)}`
    },
    isSubmitDisabled() {
      return this.isNominalBelowMinimum()
        || this.insufficientBalance()
        || this.submitLoading
    },
  },
  watch: {
    value(newValue) {
      this.localVisible = newValue

      if (newValue) {
        this.resetForm()
        this.fetchMinimumTopup()
        this.$emit('open')
      }
    },
    localVisible(newValue) {
      if (newValue !== this.value) {
        this.$emit('input', newValue)
      }
    },
  },
  mounted() {
    this.localVisible = this.value
  },
  methods: {
    isNominalBelowMinimum() {
      return this.nominalNumber < this.minimumTopupAmount
    },
    insufficientBalance() {
      return this.nominalNumber > Number(this.balance || 0)
    },
    onlyNumber(event) {
      const charCode = event.which ? event.which : event.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
      }
    },
    async fetchMinimumTopup() {
      try {
        const response = await newAxiosIns.get('/komed/api/v1/komed/balance')
        const minimumTopup = response?.data?.data?.minimum_topup ?? 0
        this.minimumTopupAmount = Number(minimumTopup || 0)
      } catch (error) {
        this.minimumTopupAmount = Number(this.minimumTopupAmount || 0)
      }
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('id-ID')
    },
    onNominalInput(value) {
      this.isNominalDirty = true
      const raw = String(value || '').replace(/\D/g, '')
      this.nominal = raw ? this.formatCurrency(raw) : ''
    },
    setTopupNominal(amount) {
      this.nominal = this.formatCurrency(amount)
    },
    closeDrawer() {
      this.localVisible = false
      this.$emit('close')
    },
    handleHidden() {
      this.localVisible = false
      this.resetForm()
      this.$emit('hidden')
    },
    handleSubmit() {
      this.submitAttempted = true
      if (this.isSubmitDisabled) return

      this.$emit('submit', { nominal: this.nominalNumber })
    },
    resetForm() {
      this.nominal = ''
      this.submitAttempted = false
      this.isNominalDirty = false
    },
  },
}
</script>

<style scoped>
::v-deep .custom-floating-drawer {
  margin: 16px !important;
  height: calc(100vh - 32px) !important;
  border-radius: 16px !important;
}

.topup-drawer-content {
  font-size: 14px;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #E2E8F0 !important;
  background-color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #626262 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02) !important;
}

.balance-box {
  border-radius: 12px !important;
  border: 1px solid #E2E8F0 !important;
  background-color: #ffffff !important;
  padding: 16px 20px !important;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.balance-label {
  color: #8c8c8c;
  font-size: 13px;
}

.balance-amount {
  color: #0A0A0A;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
}

.custom-input-topup {
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  height: 48px !important;
  box-shadow: none !important;
  font-size: 15px !important;
  color: #222222 !important;
  background-color: #FFFFFF !important;
}

.custom-input-topup.is-invalid {
  border-color: #EF4444 !important;
}

.custom-input-topup:focus {
  border-color: #F97316 !important;
  box-shadow: 0 0 0 0.2rem rgba(249, 115, 22, 0.15) !important;
}

.preset-btn {
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  color: #222222 !important;
  background-color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.2s ease !important;
}

.preset-btn:hover {
  border-color: #F97316 !important;
  color: #F97316 !important;
  background-color: #FFF7ED !important;
}

.btn-cancel {
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  background-color: #ffffff !important;
  color: #222222 !important;
  font-weight: 500 !important;
  height: 44px !important;
  padding: 0 20px !important;
}

.btn-cancel:hover {
  background-color: #F8FAFC !important;
}

.btn-submit {
  border-radius: 8px !important;
  background-color: #F97316 !important;
  border-color: #F97316 !important;
  color: #ffffff !important;
  font-weight: 500 !important;
  height: 44px !important;
  padding: 0 20px !important;
}

.btn-submit:hover {
  background-color: #EA580C !important;
  border-color: #EA580C !important;
}

.btn-submit:disabled {
  background-color: #E2E8F0 !important;
  border-color: #E2E8F0 !important;
  color: #94A3B8 !important;
  cursor: not-allowed !important;
}
</style>
